import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { GroupResponseSchema } from "../response/IndexGroupResponse";

/**
 * Mendefinisikan tipe data untuk objek filter yang didukung oleh API.
 */
interface GroupFilterProps {
    id?: string;   // Dapat berisi beberapa ID, dipisahkan koma
    name?: string;
    company_id?: string; // ID perusahaan untuk filter grup
}

/**
 * Mendefinisikan semua properti yang bisa diterima oleh hook useIndexGroups.
 * Ini mencakup semua parameter yang didukung oleh endpoint GET /group.
 */
interface IndexGroupsProps {
    filters?: GroupFilterProps;
    include?: string; // contoh: "users,company"
    search?: string;
    orderBy?: string;
    sort?: 'asc' | 'desc';
    limit?: number;
    page?: number;
    all?: boolean;
    enabled?: boolean; // Untuk mengontrol apakah query harus dijalankan
}

/**
 * Custom hook untuk mengambil daftar grup dari API.
 * Menggunakan @tanstack/react-query untuk caching, re-fetching, dan state management.
 *
 * @param query - Objek yang berisi parameter untuk kustomisasi query API.
 * @returns Hasil dari useQuery, termasuk data, isLoading, isError, dll.
 */
const useIndexGroups = (query: IndexGroupsProps) => {
    return useQuery({
        // Kunci query dibuat dinamis berdasarkan objek 'query'.
        // Ini memastikan bahwa React Query akan membuat cache terpisah untuk setiap
        // kombinasi filter dan akan me-refetch data saat parameter berubah.
        queryKey: ['groups', query],

        queryFn: async () => {
            try {
                // Membangun objek parameter untuk dikirim bersama request Axios.
                // Nama kunci parameter disesuaikan dengan spesifikasi API (misal: 'filter[name]').
                const params: Record<string, any> = {
                    'filter[id]': query.filters?.id,
                    'filter[name]': query.filters?.name,
                    'filter[company_id]': query.filters?.company_id,
                    include: query.include,
                    search: query.search,
                    orderBy: query.orderBy ?? 'created_at', // Default sorting
                    sort: query.sort ?? 'desc',             // Default sorting
                    limit: query.limit ?? 10,               // Default limit
                    page: query.page,
                    all: query.all ? 1 : undefined,
                };

                // Membersihkan parameter yang tidak memiliki nilai (undefined atau null)
                // agar tidak dikirim sebagai query string kosong ke API.
                Object.keys(params).forEach(key =>
                    (params[key] === undefined || params[key] === null) && delete params[key]
                );

                const response = await privateApi.get(`/v4/group`, { params });

                // Validasi respons dari API menggunakan skema Zod.
                const validationResult = GroupResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("API Response Validation Failed:", validationResult.error.errors);
                    // Melempar error jika data tidak sesuai dengan skema yang diharapkan.
                    throw new Error("Invalid data format received from the server for groups.");
                }

                // Mengembalikan data yang sudah divalidasi dan terjamin tipenya.
                return validationResult.data;
            } catch (error) {
                console.error("Failed to fetch groups:", error);
                // Melempar kembali error agar bisa ditangani oleh React Query.
                throw error;
            }
        },
        enabled: query.enabled !== false, // Default true, bisa di-disable dengan mengatur enabled ke false
        // Konfigurasi tambahan untuk useQuery sesuai praktik terbaik.
        retry: 1, // Hanya coba lagi 1 kali jika request gagal.
        refetchOnWindowFocus: false, // Tidak me-refetch saat window kembali fokus.
    });
};

export default useIndexGroups;